import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useRef,
  useState,
} from "react";

import { PublicSvgIcon } from "~/components/public_svg_icon";

export type Select2Size = "sm" | "md";
export type Select2Variant = "outline" | "filled";

export interface Select2Props {
  label: string;
  value?: ReactNode;
  active?: boolean;
  iconName?: string;
  // Custom leading icon node. Takes precedence over `iconName` when set.
  icon?: ReactNode;
  showCaret?: boolean;
  onClear?: () => void;
  renderPopover?: (close: () => void) => ReactNode;
  renderPopoverHeader?: (close: () => void) => ReactNode;
  onToggle?: () => void;
  ariaLabel?: string;
  // Defaults to "md" at ≥sm breakpoint, "sm" below.
  size?: Select2Size;
  // When true, chip spans the container width and the value text region grows to fill.
  fullWidth?: boolean;
  // "outline" = dashed-border pill (default). "filled" = primary[600] bg + white text.
  variant?: Select2Variant;
  // Applied to the trigger chip element.
  dataTestId?: string;
}

export function Select2({
  label,
  value,
  active = false,
  iconName,
  icon,
  showCaret = true,
  onClear,
  renderPopover,
  renderPopoverHeader,
  onToggle,
  ariaLabel,
  size,
  fullWidth = false,
  variant = "outline",
  dataTestId,
}: Select2Props) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const theme = useTheme();
  const isUpSm = useMediaQuery(theme.breakpoints.up("sm"));
  const effectiveSize: Select2Size = size ?? (isUpSm ? "md" : "sm");
  const iconPx = effectiveSize === "md" ? 18 : 16;
  const caretPx = effectiveSize === "md" ? 16 : 12;

  const isToggleMode = renderPopover === undefined;
  const hasEffectiveCaret = showCaret && !isToggleMode;

  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleBodyClick = useCallback(() => {
    if (isToggleMode) {
      onToggle?.();
    } else {
      handleOpen();
    }
  }, [handleOpen, isToggleMode, onToggle]);

  const handleClear = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onClear?.();
    },
    [onClear],
  );

  const leadingIconName =
    iconName ?? (!isToggleMode && !active && !icon ? "add" : undefined);

  const isFilled = variant === "filled";

  const chipLabel = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        ...(fullWidth ? { flex: 1, width: "100%" } : {}),
      }}
    >
      <Box
        component="span"
        sx={{
          color: "inherit",
          ...(fullWidth ? { flex: 1, minWidth: 0 } : {}),
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Box>
      {active && value !== undefined && value !== null && value !== "" && (
        <Typography
          component="span"
          variant={effectiveSize === "md" ? "body2" : "body3"}
          sx={(t) => ({
            pl: 0.5,
            ml: 0.25,
            borderLeft: `1px solid ${
              isFilled ? t.palette.common.white : t.palette.primary.main
            }`,
            color: isFilled ? t.palette.common.white : t.palette.primary.dark,
            maxWidth: 180,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {value}
        </Typography>
      )}
      {hasEffectiveCaret && !(active && onClear) && (
        <Box
          aria-hidden
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: caretPx,
            height: caretPx,
            ml: fullWidth ? "auto" : 0,
          }}
        >
          <svg
            width={caretPx}
            height={caretPx}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isOpen ? (
              <path
                d="M11.7579 9.84519L7.15795 14.4452L6.4502 13.7374L11.7579 8.42969L17.0657 13.7374L16.3579 14.4452L11.7579 9.84519Z"
                fill={theme.palette.primary.main}
              />
            ) : (
              <path
                d="M12.0001 14.7108L6.69238 9.40306L7.40013 8.69531L12.0001 13.2953L16.6001 8.69531L17.3079 9.40306L12.0001 14.7108Z"
                fill={theme.palette.action.active}
              />
            )}
          </svg>
        </Box>
      )}
    </Box>
  );

  const ariaProps = isToggleMode
    ? { "aria-pressed": active }
    : { "aria-haspopup": "menu" as const, "aria-expanded": isOpen };

  return (
    <>
      <Chip
        ref={anchorRef}
        variant="filter"
        color={active ? "primary" : "default"}
        clickable
        onClick={handleBodyClick}
        aria-label={ariaLabel ?? label}
        {...(dataTestId ? { "data-testid": dataTestId } : {})}
        {...ariaProps}
        icon={
          icon ? (
            <Box
              component="span"
              aria-hidden
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
          ) : leadingIconName ? (
            <Box
              component="span"
              aria-hidden
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PublicSvgIcon
                name={leadingIconName}
                size={iconPx}
                inheritColor
              />
            </Box>
          ) : undefined
        }
        label={chipLabel}
        onDelete={active && onClear ? handleClear : undefined}
        deleteIcon={
          active && onClear ? (
            <Box
              component="span"
              aria-label={`Clear ${label}`}
              role="button"
              sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -8,
                  bottom: -8,
                  left: -8,
                  right: -8,
                },
              }}
            >
              <PublicSvgIcon name="close" size={13} inheritColor />
            </Box>
          ) : undefined
        }
        sx={[
          {
            py: effectiveSize === "md" ? 2 : 1.25,
            px: 1.5,
            typography: effectiveSize === "md" ? "body2" : "body3",
            "& .MuiChip-icon": {
              marginLeft: 0,
              marginRight: 1,
            },
            "& .MuiChip-label": {
              paddingLeft: 0,
            },
          },
          isFilled &&
            ((t) => ({
              backgroundColor: t.palette.primary[600],
              color: t.palette.common.white,
              border: 0,
              borderStyle: "solid",
              "&:hover": {
                backgroundColor: t.palette.primary[700],
              },
              "& .MuiChip-icon": { color: t.palette.common.white },
              "& .MuiChip-deleteIcon": { color: t.palette.common.white },
            })),
          fullWidth && {
            width: "100%",
            "& .MuiChip-label": { flex: 1, width: "100%" },
          },
        ]}
      />
      {renderPopover && (
        <Menu
          open={isOpen}
          anchorEl={anchorRef.current}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: (t) => ({
                minWidth: 240,
                marginTop: 0.75,
                borderRadius: 1.5,
                boxShadow: t.shadows[2],
                pt: 4,
                pb: 3,
                px: 4,
                overflow: "visible",
                "@media (max-height: 700px)": {
                  overflow: "auto",
                  maxHeight: "calc(100vh - 16px)",
                },
                "& .MuiList-root": {
                  padding: 0,
                },
              }),
            },
            list: {
              "aria-label": label,
            },
          }}
        >
          <Stack component="div">
            {renderPopoverHeader && (
              <Box sx={{ px: 1.25, pt: 1, pb: 1 }}>
                {renderPopoverHeader(handleClose)}
              </Box>
            )}
            {renderPopover(handleClose)}
          </Stack>
        </Menu>
      )}
    </>
  );
}
